import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import PauseIcon from '../svgIcon/PauseIcon';
import PlayIcon from '../svgIcon/PlayIcon';
import './WaveformSingersss.css';

const formWaveSurferOptions = ref => ({
	container: ref,
	waveColor: '#000000',
	progressColor: '#bababa',
	cursorColor: '#000',
	barWidth: 2,
	barGap: 2,
	barRadius: 0,
	responsive: true,
	height: 160,
	// If true, normalize by the maximum peak instead of 1.0.
	normalize: true,
	// Use the PeakCache to improve rendering speed of large waveforms.
	partialRender: true
});

// Pseudo-random-but-stable bar heights for the loading skeleton.
// Bar count is tuned to roughly match the visual density of the actual
// rendered wavesurfer waveform.
const LOADER_BARS = Array.from({ length: 110 }, (_, i) => {
	const h = 30 + Math.abs(Math.sin(i * 0.7) * Math.cos(i * 0.31)) * 70;
	return Math.round(h);
});

export default function WaveformSinger({ story }) {
	const waveformRef = useRef();
	const wavesurfer = useRef();
	const [waveDisabled, setDisabled] = useState(false);
	const [playing, setPlay] = useState(false);
	const [volume, setVolume] = useState(0.5);
	const [isPause, setPause] = useState(false);
	const [total, setTotal] = useState(0);
	// If the API ships pre-computed peaks (story.waveform_data), wavesurfer
	// renders the waveform almost instantly — start with the loader hidden.
	const [waveformReady, setWaveformReady] = useState(!!story?.waveform_data);
	useEffect(() => {
		setPlay(true);
		const audio = document.getElementById('audio');
		const range = document.getElementById('slider');

		audio.addEventListener('timeupdate', e => {
			setTotal(audio.duration);
			if (!isPause) {
				const totalTime = audio.duration;
				const _currentTime = audio.currentTime;
				const progress = _currentTime / totalTime;
				try {
					wavesurfer.current.seekTo(progress);
				} catch (error) {
					console.log(error);
				}
			}
		});

		// Sync the play-button icon to the actual <audio> element state, so
		// it reflects autoPlay-driven playback / external pauses correctly
		// regardless of how we got there.
		const onAudioPlay = () => {
			setPlay(false);
			setPause(false);
		};
		const onAudioPause = () => {
			setPlay(true);
			setPause(true);
		};
		audio.addEventListener('play', onAudioPlay);
		audio.addEventListener('pause', onAudioPause);

		// Trigger playback as soon as the audio element has enough data —
		// the <audio autoPlay> attribute already does this on most browsers,
		// but a manual nudge here covers the cases (especially Chrome with
		// pre-computed wavesurfer peaks) where autoPlay would otherwise
		// stall waiting for a user gesture even though the data is ready.
		const tryPlay = () => {
			const p = audio.play();
			if (p && typeof p.then === 'function') {
				p.catch(() => {
					/* autoplay blocked — fall back to the play button */
				});
			}
		};
		audio.addEventListener('canplay', tryPlay, { once: true });
		const options = formWaveSurferOptions(waveformRef.current);
		wavesurfer.current = WaveSurfer.create(options);

		// Register the 'ready' handler BEFORE load() — for pre-computed peaks
		// the event can fire synchronously and we'd otherwise miss it.
		wavesurfer.current.on('ready', () => {
			if (wavesurfer.current) {
				wavesurfer.current.setVolume(0);
				setVolume(0);
				setDisabled(false);
				setWaveformReady(true);
			}
			// Try to start playback automatically once the audio is ready.
			// Browsers can block unmuted autoplay; if so we leave the play
			// button visible so the user can start it manually.
			const audioEl = document.getElementById('audio');
			if (audioEl) {
				const playPromise = audioEl.play();
				if (playPromise && typeof playPromise.then === 'function') {
					playPromise
						.then(() => {
							// `playing=false` makes the button show the pause icon
							// (i.e., audio is currently playing).
							setPlay(false);
							setPause(false);
						})
						.catch(() => {
							// Autoplay blocked — keep the play icon visible.
						});
				}
			}
		});

		if (story.waveform_data) {
			wavesurfer.current.load(story.audio_url, story.waveform_data.data);
			setVolume(volume);
		} else {
			wavesurfer.current.load(story.audio_url);
		}
		range.addEventListener('change', e => {
			audio.currentTime = range.value;
		});
		setPlay(true);

		return () => wavesurfer.current.destroy();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handlePlayPause = e => {
		const _audio = document.getElementById('audio');
		setPlay(!playing);
		//wavesurfer.current.playPause();
		if (playing) {
			_audio.play();
			setPause(false);
		} else {
			_audio.pause();
			setPause(true);
		}
	};

	return (
		<div className="containers">
			<div className="meta">
				{story?.title && <div className="title">{story.title}</div>}
				{story?.subtitle && <div className="sub-title">{story.subtitle}</div>}
			</div>

			<input type="range" name="" id="slider" min={0} max={total} className="scroll-audio" />
			<div className="audio-wave">
				{!waveformReady && (
					<div className="waveform-loader" aria-hidden="true">
						{LOADER_BARS.map((h, i) => (
							<div
								key={i}
								className="waveform-loader-bar"
								style={{
									height: `${h}%`,
									animationDelay: `${(i % 12) * 0.08}s`
								}}
							/>
						))}
					</div>
				)}
				<div id="waveform" ref={waveformRef} />
			</div>

			<div className="controls">
				{/* eslint-disable-next-line react/button-has-type */}
				<button
					id="btnAudio"
					className={`${waveDisabled ? 'spinner disabled' : 'button'}`}
					onClick={handlePlayPause}
				>
					{!playing ? (
						<div className="icon pause-icon">
							<PauseIcon />
						</div>
					) : (
						<div className="icon play-icon">
							<PlayIcon />
						</div>
					)}
				</button>
			</div>
			<audio id="audio" style={{ opacity: '0' }}>
				<track kind="captions" controls="controls" />
				<source src={story.audio_url} type="audio/ogg" />
				<source src={story.audio_url} type="audio/mpeg" />
				Your browser does not support the audio element.
			</audio>
		</div>
	);
}
